import { memo, useRef } from 'react'
import styled from 'styled-components'

import { calendarScrollerStyles } from 'tripPlanner/components/Calendar/calendarPanelLayout'
import { rem } from 'polished'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import FormattedOccupancy from 'components/Common/OccupancyPicker/FormattedOccupancy'
import BodyText from 'LuxKit/Typography/BodyText'
import SearchDatesPicker from 'components/SearchV2/Components/Mobile/DateSearchFloatingPanel/SearchDatePicker'
import TripContextSearchCalendar from 'tripPlanner/components/Calendar/TripContextSearchCalendar'
import FlexibleDatePicker from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker'
import SearchDateToggle from 'components/Search/SearchForm/SearchDateInput/SearchDateToggle'
import Group from 'components/utils/Group'
import FilterChipsScrollContainer from 'tripPlanner/components/TripItemFilters/FilterChipsScrollContainer'
import { toCalendarFocusDate } from 'components/Common/Calendar/calendarFocusMonth'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import type { ChatHotelCarouselFiltersProps } from './ChatHotelCarouselFilters.types'

const DropdownWidthSpacer = styled.div`
  width: ${rem(700)};
`

const OccupancyPickerContainer = styled.div`
  width: ${rem(700)};
`

/** The panel's own chrome: what is kept for the toggle, footer and padding. */
const PANEL_CHROME_HEIGHT_PX = 190

const CalendarScroller = styled.div`
  ${calendarScrollerStyles(PANEL_CHROME_HEIGHT_PX)}
`

interface Props extends ChatHotelCarouselFiltersProps {}

function ChatHotelCarouselDesktopFilters({
  cardType,
  canShowOccupancyFilter,
  canShowDateFilter,
  filterActive,
  occupancy,
  dateFilter,
  focusMonth,
  tripStops,
  applyLabel,
  clearFiltersLabel,
  onClearFilters,
}: Props) {
  const hasTripContext = !!tripStops?.length
  // The calendar is the one thing that scrolls, so the toggle above it stays
  // put while months run past underneath.
  const calendarScrollRef = useRef<HTMLDivElement>(null)
  const occupancyPickerFooter = (
    <Group direction="horizontal" horizontalAlign="end">
      <BodyText variant="large">
        <FormattedOccupancy occupancies={occupancy.pendingRooms} />
      </BodyText>
    </Group>
  )

  const dateFilterForm = (
    <Group direction="vertical" gap={24}>
      <Group direction="horizontal" horizontalAlign="center">
        <SearchDateToggle
          onSelectSpecific={dateFilter.handleSelectSpecific}
          onSelectFlexible={dateFilter.handleSelectFlexible}
          onSelectAnytime={dateFilter.handleSelectAnytime}
          anytimeDateSelected={dateFilter.pendingDateMode === DATE_SEARCH_OPTION_IDS.ANYTIME}
          dateSearchOptionId={dateFilter.pendingDateMode}
        />
      </Group>
      {dateFilter.shouldShowFlexiblePicker && (
        <FlexibleDatePicker
          onDurationChange={dateFilter.handlePendingFlexibleNightsChange}
          onMonthChange={dateFilter.handlePendingFlexibleMonthsChange}
          previouslySelectedDuration={dateFilter.pendingFlexibleNights}
          previouslySelectedMonths={dateFilter.pendingFlexibleMonths || undefined}
        />
      )}
      {dateFilter.shouldShowSpecificPicker && hasTripContext && (
        <CalendarScroller ref={calendarScrollRef}>
          <TripContextSearchCalendar
            tripStops={tripStops}
            onDatesChange={dateFilter.handlePendingDatesChange}
            startDate={dateFilter.pendingCheckIn}
            endDate={dateFilter.pendingCheckOut}
            minDate={dateFilter.today}
            // The extent, not the stay cap — the calendar scrolls through
            // every month, so the range must not shrink mid-selection.
            maxDate={dateFilter.calendarExtentMaxDate}
            maxNights={dateFilter.maxStayNights}
            allowSameDayRange={cardType === 'experience'}
            focusMonth={toCalendarFocusDate(focusMonth)}
            scrollContainerRef={calendarScrollRef}
          />
        </CalendarScroller>
      )}
      {dateFilter.shouldShowSpecificPicker && !hasTripContext && (
        <SearchDatesPicker
          type="single"
          onDatesChange={dateFilter.handlePendingDatesChange}
          startDate={dateFilter.pendingCheckIn}
          endDate={dateFilter.pendingCheckOut}
          minDate={dateFilter.today}
          maxDate={dateFilter.calendarMaxDate}
          allowSameDayRange={cardType === 'experience'}
        />
      )}
    </Group>
  )

  return (
    <FilterChipsScrollContainer>
      {canShowOccupancyFilter && (
        <>
          <AssistChip
            ref={occupancy.triggerRef}
            variant="outline"
            size="medium"
            endIcon={<DropdownChevron open={occupancy.open} />}
            onClick={occupancy.handleToggle}
          >
            <FormattedOccupancy occupancies={occupancy.rooms} />
          </AssistChip>
          <DropdownSheet
            size="content"
            triggerRef={occupancy.triggerRef}
            anchorRef={occupancy.triggerRef}
            open={occupancy.open}
            onClose={occupancy.handleClose}
            portaled
            primaryActionProps={{
              children: applyLabel,
              type: 'button',
              onClick: occupancy.handleApply,
            }}
            footerStart={occupancyPickerFooter}
          >
            <OccupancyPickerContainer>
              <OccupancyPicker
                occupancies={occupancy.pendingRooms}
                onChange={occupancy.handlePendingChange}
                enableMultiRoom
                childVariant="child-age"
              />
            </OccupancyPickerContainer>
          </DropdownSheet>
        </>
      )}
      {canShowDateFilter && (
        <>
          <AssistChip
            ref={dateFilter.triggerRef}
            variant="outline"
            size="medium"
            endIcon={<DropdownChevron open={dateFilter.open} />}
            onClick={dateFilter.handleToggle}
          >
            {dateFilter.chipLabel}
          </AssistChip>
          <DropdownSheet
            size="content"
            placement="bottom"
            crossAxisShift
            autoHeight
            triggerRef={dateFilter.triggerRef}
            anchorRef={dateFilter.triggerRef}
            open={dateFilter.open}
            onClose={dateFilter.handleClose}
            portaled
            primaryActionProps={{
              children: applyLabel,
              type: 'button',
              disabled: dateFilter.isApplyDisabled,
              onClick: dateFilter.handleApply,
            }}
          >
            <DropdownWidthSpacer />
            {dateFilterForm}
          </DropdownSheet>
        </>
      )}
      {filterActive && (
        <AssistChip
          variant="filled"
          size="medium"
          endIcon={<LineTimesIcon />}
          onClick={onClearFilters}
        >
          {clearFiltersLabel}
        </AssistChip>
      )}
    </FilterChipsScrollContainer>
  )
}

export default memo(ChatHotelCarouselDesktopFilters)
